import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useApi } from '../use_api';
import {
  get_members,
  approve_member,
  decline_member,
  set_member_status,
  set_member_role,
  ApiError,
  type MemberInfo,
} from '../api';

// Tenant-level Members section (docs/archive/onboarding-phase2.md §3) — list the workspace's users,
// approve/decline pending joins (the pilot-critical actions), and manage active members.
// Owner/admin only: the nav link and route are role-gated, and the BFF enforces the same gate
// server-side (require_write_role) with tenant scope from the session claim.

const TH_STYLE = {
  textAlign: 'left' as const,
  fontSize: 12,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  color: 'var(--muted)',
  padding: '8px 12px 4px 0',
};
const TD_STYLE = { padding: '8px 12px 8px 0', borderTop: '1px solid var(--line)', fontSize: 14 };

function role_label(role: MemberInfo['role']): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function PendingCard({
  member,
  busy,
  onApprove,
  onDecline,
}: {
  member: MemberInfo;
  busy: boolean;
  onApprove: (enable_app?: boolean) => void;
  onDecline: () => void;
}) {
  // Default-checked while apps are unbilled (decision 3) — revisit at billing.
  const [enable, setEnable] = useState(true);
  const offer = member.enable_app_offer;
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <strong>
            {member.first_name} {member.last_name}
          </strong>
          <div className="muted" style={{ fontSize: 13 }}>{member.email}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" disabled={busy} onClick={() => onApprove(offer ? enable : undefined)}>
            Approve
          </button>
          <button className="btn secondary" disabled={busy} onClick={onDecline}>
            Decline
          </button>
        </div>
      </div>
      {offer && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 12 }}>
          <input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} />
          <span>Enable {offer.display_name} for your whole workspace</span>
        </label>
      )}
    </div>
  );
}

export function MembersView() {
  const { state } = useAuth();
  const { loading, data, error, reload } = useApi(get_members, []);
  const [busy_id, setBusy] = useState<string | null>(null);
  const [action_error, setActionError] = useState<string | null>(null);

  if (state.status !== 'signed_in') return null;
  const my_role = state.session.role;

  const act = (user_id: string, fn: () => Promise<unknown>) => {
    setBusy(user_id);
    setActionError(null);
    fn()
      .then(() => reload())
      .catch((e) => setActionError(e instanceof ApiError ? e.reason : 'request_failed'))
      .finally(() => setBusy(null));
  };

  if (loading) return <div className="muted">Loading members…</div>;
  if (error) return <div className="error">Couldn't load members ({error}).</div>;

  const users = data?.users ?? [];
  const pending = users.filter((u) => u.status === 'pending');
  const others = users.filter((u) => u.status !== 'pending');

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ marginTop: 0 }}>Members</h2>
      {action_error && <p className="error">Action failed ({action_error}). Try again.</p>}

      {pending.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Waiting for approval
          </h3>
          {pending.map((m) => (
            <PendingCard
              key={m.user_id}
              member={m}
              busy={busy_id === m.user_id}
              onApprove={(enable_app) => act(m.user_id, () => approve_member(m.user_id, enable_app))}
              onDecline={() => act(m.user_id, () => decline_member(m.user_id))}
            />
          ))}
        </section>
      )}

      <div className="card">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH_STYLE}>Name</th>
              <th style={TH_STYLE}>Email</th>
              <th style={TH_STYLE}>Role</th>
              <th style={TH_STYLE}>Status</th>
              <th style={TH_STYLE} />
            </tr>
          </thead>
          <tbody>
            {others.map((m) => {
              // Owner rows have no actions (role/status of the owner is not editable here);
              // self-targeting is additionally blocked server-side (409 self_target).
              const can_manage = m.role !== 'owner' && my_role !== 'member';
              return (
                <tr key={m.user_id}>
                  <td style={TD_STYLE}>
                    {m.first_name} {m.last_name}
                  </td>
                  <td style={TD_STYLE}>{m.email}</td>
                  <td style={TD_STYLE}>{role_label(m.role)}</td>
                  <td style={TD_STYLE}>
                    {m.status === 'deactivated' ? <span className="muted">Deactivated</span> : 'Active'}
                  </td>
                  <td style={{ ...TD_STYLE, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {can_manage && (
                      <>
                        <button
                          className="btn secondary"
                          style={{ fontSize: 12, padding: '4px 10px', marginRight: 8 }}
                          disabled={busy_id === m.user_id}
                          onClick={() =>
                            act(m.user_id, () =>
                              set_member_role(m.user_id, m.role === 'admin' ? 'member' : 'admin'),
                            )
                          }
                        >
                          {m.role === 'admin' ? 'Make member' : 'Make admin'}
                        </button>
                        <button
                          className="btn secondary"
                          style={{ fontSize: 12, padding: '4px 10px' }}
                          disabled={busy_id === m.user_id}
                          onClick={() =>
                            act(m.user_id, () =>
                              set_member_status(m.user_id, m.status === 'deactivated' ? 'active' : 'deactivated'),
                            )
                          }
                        >
                          {m.status === 'deactivated' ? 'Reactivate' : 'Deactivate'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
